import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { api, ApiError } from '../lib/api';

/** Einladung annehmen: Passwort vergeben und loslegen. */
export function InvitePage() {
  const params = useParams({ strict: false }) as { token?: string };
  const token = params.token ?? '';
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const accept = useMutation({
    mutationFn: () => api('/auth/invite/accept', { method: 'POST', body: { token, password } }),
    onSuccess: () => setDone(true),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Das hat nicht geklappt'),
  });

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = repeat.length > 0 && password !== repeat;

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="animate-fade-up mb-6 text-center">
          <h1 className="font-[family-name:var(--font-display)] text-3xl">Willkommen</h1>
          <p className="mt-1 text-sm text-[var(--color-fg3)]">
            Vergib ein Passwort für die Khadi Inventarverwaltung.
          </p>
        </div>

        <div className="surface animate-fade-up p-6">
          {done ? (
            <div className="text-center">
              <CheckCircle2
                className="mx-auto mb-3 h-8 w-8 text-[var(--color-khadi-forest-600)]"
                aria-hidden
              />
              <p className="mb-4 text-sm">Das Passwort ist gesetzt.</p>
              <a href="/anmelden" className="btn btn-primary btn-lg w-full no-underline">
                Zur Anmeldung
              </a>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                setError(null);
                accept.mutate();
              }}
            >
              <div>
                <label className="label" htmlFor="invite-password">
                  Neues Passwort
                </label>
                <input
                  id="invite-password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  className={`field ${tooShort ? 'field-invalid' : ''}`}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <p className="mt-1 text-sm text-[var(--color-fg3)]">
                  Mindestens 12 Zeichen, Groß- und Kleinbuchstaben sowie eine Ziffer.
                </p>
              </div>

              <div>
                <label className="label" htmlFor="invite-repeat">
                  Wiederholen
                </label>
                <input
                  id="invite-repeat"
                  type="password"
                  autoComplete="new-password"
                  className={`field ${mismatch ? 'field-invalid' : ''}`}
                  value={repeat}
                  onChange={(event) => setRepeat(event.target.value)}
                  required
                />
                {mismatch && (
                  <p className="mt-1 text-sm text-[var(--color-error)]">
                    Die Eingaben stimmen nicht überein.
                  </p>
                )}
              </div>

              {error && (
                <p className="rounded-[var(--radius-sm)] bg-[var(--color-khadi-clay-100)] px-5 py-4 text-sm text-[var(--color-khadi-clay-700)]">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-lg w-full"
                disabled={accept.isPending || tooShort || mismatch || !password || !repeat}
              >
                <KeyRound className="h-4 w-4" aria-hidden />
                Passwort speichern
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
